import { ArdoqId } from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { Position } from '@ardoq/component-hierarchies';
import { actionCreator } from '@ardoq/rxbeach';
import { OfferableReference } from './offerableReferences';
import { SourceBox, SourceHandle } from './sourceAnchor';

export type DynamicReferenceCreationArmedPayload = {
  sourceComponentId: ArdoqId;
  /** Where the tether starts until the source itself can be located. */
  startPosition: Position;
  /**
   * The occurrence the gesture started on. A grouped view renders the same
   * component once per group, so its id cannot say which of them to follow.
   */
  sourceHandle: SourceHandle | null;
};

/** Where the target was picked: among the search box's results or on the canvas. */
export type TargetOrigin = 'search' | 'canvas';

export type DynamicReferenceCreationDroppedPayload = {
  /** Where the picker opens, in client coordinates. */
  dropPosition: Position;
  /**
   * Where the source was when the pointer was released, and the box it was
   * measured against, so the picker draws and masks its tether exactly as the
   * last frame of the drag did. `null` if it could not be located then. The
   * picker is pinned and takes this rather than reading the element again,
   * which by then may have been culled or re-rendered away.
   */
  sourceBox: SourceBox | null;
  targetComponentId: ArdoqId;
  targetOrigin: TargetOrigin;
  /**
   * What was offerable when the pointer was released. Carried rather than read
   * again, because the resolution keeps following the pointer afterwards.
   */
  references: OfferableReference[];
};

export const dynamicReferenceCreationArmed =
  actionCreator<DynamicReferenceCreationArmedPayload>(
    '[dynamicReferenceCreation] armed'
  );

export const dynamicReferenceCreationDropped =
  actionCreator<DynamicReferenceCreationDroppedPayload>(
    '[dynamicReferenceCreation] dropped'
  );

export type DynamicReferenceCreationOptionSelectedPayload = {
  reference: OfferableReference;
  /**
   * Whether the row sat above the label or under it. Only tracking reads it:
   * it tells us whether people create what the viewpoint asks for or reach
   * past it.
   */
  isInView: boolean;
  targetOrigin: TargetOrigin;
};

/** The gesture ends on selection, so the picker closes before the batch lands. */
export const dynamicReferenceCreationOptionSelected =
  actionCreator<DynamicReferenceCreationOptionSelectedPayload>(
    '[dynamicReferenceCreation] option selected'
  );

/**
 * The create is over, whichever way it went, and the reference it made is in
 * the loaded graph - so the next gesture's picker can see it.
 */
export const dynamicReferenceCreationCreateSettled = actionCreator(
  '[dynamicReferenceCreation] create settled'
);

/**
 * Where the source is, from the surface that draws it. The navigator knows
 * where every row would be whether or not it is rendered, so it says rather
 * than being asked - ARD-34358 is about every surface answering this way.
 */
export const dynamicReferenceCreationSourceMoved = actionCreator<SourceBox>(
  '[dynamicReferenceCreation] source moved'
);

export const dynamicReferenceCreationDisarmed = actionCreator(
  '[dynamicReferenceCreation] disarmed'
);

/**
 * Why a gesture ended with nothing created. The phase is part of the answer:
 * giving up while still looking for a target is not the same as seeing what was
 * offered and walking away from it.
 */
export type GestureCancelReason =
  | 'released on no target'
  | 'escape while dragging'
  | 'escape at the picker'
  | 'clicked outside the picker'
  | 'left viewpoint mode';

export const dynamicReferenceCreationCancelRequested =
  actionCreator<GestureCancelReason>(
    '[dynamicReferenceCreation] cancel requested'
  );

/** The scope data the search box lists its results from. */
export const dynamicReferenceCreationSearchScopeDataLoaded =
  actionCreator<EnhancedScopeData>(
    '[dynamicReferenceCreation] search scope data loaded'
  );

export const dynamicReferenceCreationSearchTermChanged = actionCreator<string>(
  '[dynamicReferenceCreation] search term changed'
);
